/**
 * Safely extract a human-readable message from an Axios/API error.
 *
 * FastAPI/Pydantic validation errors (HTTP 422) return `detail` as an ARRAY of
 * objects ({type, loc, msg, input, url}). Passing that array/object straight to
 * a toast or JSX child crashes React with:
 *   "Objects are not valid as a React child (found: object with keys ...)".
 * This helper always returns a string.
 *
 * @param {*} error - Axios error (or any thrown value)
 * @param {string} fallback - message to use when nothing usable is found
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    const msgs = detail.map((e) => {
      if (typeof e === 'string') return e;
      if (e && typeof e === 'object') {
        const loc = Array.isArray(e.loc)
          ? e.loc.filter((p) => p !== 'body').join('.')
          : '';
        return loc && e.msg ? `${loc}: ${e.msg}` : (e.msg || JSON.stringify(e));
      }
      return String(e);
    });
    const joined = msgs.filter(Boolean).join(', ');
    return joined || fallback;
  }

  if (detail && typeof detail === 'object') {
    return detail.message || detail.msg || fallback;
  }

  return (
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export default getApiErrorMessage;
